import { Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";

import WarningIcon from "src/assets/svg-icons/auditing/warning.svg";
import SuccessIcon from "src/assets/svg-icons/auditing/success.svg";
import ErrorIcon from "src/assets/svg-icons/auditing/error.svg";

type SecondaryChipVariant =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "neutral";

interface IProps {
  variant?: SecondaryChipVariant;
  label?: string;
}

const VARIANT_CONFIG = {
  info: {
    color: "#4D4DEC",
    backgroundColor: "#F2F2FD",
    borderColor: "#9999F4",
    icon: (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "#4D4DEC",
        }}
      />
    ),
  },

  success: {
    color: "#17B26A",
    backgroundColor: "#ECFDF3",
    borderColor: "#A9EFC5",
    icon: <img src={SuccessIcon} alt="" />,
  },

  warning: {
    color: "#FDB022",
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
    icon: <img src={WarningIcon} alt="" />,
  },

  error: {
    color: "#B32318",
    backgroundColor: "#FEF3F2",
    borderColor: "#FDA29B",
    icon: <img src={ErrorIcon} alt="" />,
  },

  neutral: {
    color: "#344054",
    backgroundColor: "#F2F4F7",
    borderColor: "#D0D5DD",
    icon: (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "#667085",
        }}
      />
    ),
  },
} satisfies Record<
  SecondaryChipVariant,
  {
    color: string;
    backgroundColor: string;
    borderColor: string;
    icon: React.ReactNode;
  }
>;

const SecondaryChip = ({ variant = "info", label }: IProps) => {
  const { t } = useTranslation("auditing");

  const defaultLabels: Record<SecondaryChipVariant, string> = {
    info: t("new"),
    success: t("confirmed"),
    warning: t("progress"),
    error: t("issue"),
    neutral: t("neutral"),
  };

  const config = VARIANT_CONFIG[variant];

  return (
    <Chip
      label={label ?? defaultLabels[variant]}
      icon={config.icon}
       style={{
        color: config.color,
        backgroundColor: config.backgroundColor,
        border: `1px solid ${config.borderColor}`,
        padding: "4px 10px",
      }}
      sx={{
        "& .MuiChip-label": {
          fontSize: "14px",
          fontWeight: 500,
        },
      }}
    />
  );
};

export default SecondaryChip;
